import { useEffect, useState } from 'react';
import axios from 'axios';
import Layout from './components/Layout';
import { API_BASE_URL } from '../config/ApiConfig';
import PaginationBar from './components/PaginationBar';
import TableContainer from './components/TableContainer';
import DataTable from './components/DataTable';
import ResponsiveShadow from './components/ResponsiveShadow';
import useDefaultModal from './components/hooks/useDefaultModal';
import useConfirmModal from './components/hooks/useConfirmModal';
import DefaultModal from './components/modals/DefaultModal';
import ConfirmModal from './components/modals/ConfirmModal';
import ItemModal from './components/modals/ItemModal';
import usePagination10 from './components/hooks/usePagination10';
import PageHeader from './components/PageHeader';
import PageContent from './components/PageContent';
import Toolbar from './components/Toolbar';
import LoadingModal from './components/modals/LoadingModal';

const emptyForm = { idNatureza: '', idTipoBem: '', id: '', descricao: '' };

function Itens() {
  const token = localStorage.getItem('token');
  const [items, setItems] = useState([]);
  const [naturezas, setNaturezas] = useState([]);
  const [tiposBens, setTiposBens] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [showModal, setShowModal] = useState(false);
  const [selected, setSelected] = useState([]);
  const [atualizar, setAtualizar] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const { setDefaultModal, showDefaultModal } = useDefaultModal();
  const { setConfirmModal, showConfirmModal } = useConfirmModal();
  const { setPagination } = usePagination10(items.length);
  const displayed = items.slice(setPagination.startIndex, setPagination.startIndex + setPagination.rowsPerPage);

  useEffect(() => {
    const h = { Authorization: `Bearer ${token}` };
    setIsLoading(true);
    Promise.all([
      axios.get(`${API_BASE_URL.ITEM}/all`, { headers: h }),
      axios.get(`${API_BASE_URL.NATUREZA}/all`, { headers: h }),
      axios.get(`${API_BASE_URL.TIPO_BEM}/all`, { headers: h }),
    ]).then(([rItens, rNaturezas, rTipos]) => {
      const sorted = [...rItens.data].sort((a, b) =>
        String(a.idNatureza).localeCompare(String(b.idNatureza)) ||
        a.idTipoBem - b.idTipoBem ||
        a.id - b.id
      );
      setItems(sorted);
      setNaturezas(rNaturezas.data);
      setTiposBens(rTipos.data);
    }).catch(() => showDefaultModal('error', 'Erro ao carregar itens.'))
      .finally(() => setIsLoading(false));
  }, [token, atualizar]);

  function handleChange(e) { const { name, value } = e.target; setForm(f => ({ ...f, [name]: value })); }
  function handleNew() { setForm(emptyForm); setShowModal(true); }
  function handleEdit() { if (selected.length !== 1) return; setForm({ ...selected[0] }); setShowModal(true); }

  function handleSave(e) {
    e.preventDefault();
    axios.post(`${API_BASE_URL.ITEM}/save`, form, { headers: { Authorization: `Bearer ${token}` } })
      .then(() => {
        setShowModal(false);
        setAtualizar(a => !a);
        setSelected([]); // Limpa seleção após salvar
      })
      .catch(() => showDefaultModal('error', 'Erro ao salvar item.'));
  }

  function handleDelete() {
    if (selected.length === 0) return;
    showConfirmModal('delete', () => Promise.all(selected.map(s => axios.delete(`${API_BASE_URL.ITEM}/delete`, {
      params: { idNatureza: s.idNatureza, idTipoBem: s.idTipoBem, id: s.id },
      headers: { Authorization: `Bearer ${token}` }
    }))).then(() => { setSelected([]); setAtualizar(a => !a); })
      .catch(() => showDefaultModal('error', 'Erro ao excluir item.'))
    );
  }

  function keyOf(item) { return `${item.idNatureza}_${item.idTipoBem}_${item.id}`; }
  function toggleSelect(item) {
    setSelected(prev => prev.find(s => keyOf(s) === keyOf(item)) ? [] : [item]);
  }

  const grouped = naturezas
    .filter(n => displayed.some(item => String(item.idNatureza) === String(n.id)))
    .map(n => ({
      natureza: n,
      tipos: tiposBens
        .filter(t => String(t.idNatureza) === String(n.id) && displayed.some(item => String(item.idNatureza) === String(n.id) && item.idTipoBem === t.id))
        .map(t => ({
          tipoBem: t,
          itens: displayed.filter(item => String(item.idNatureza) === String(n.id) && item.idTipoBem === t.id),
        })),
    }));

  return (
    <Layout>
      <LoadingModal show={isLoading} />
      <ResponsiveShadow>
        <PageHeader title="ITENS" icon="bi-list-ul" />
        <PageContent>
          <Toolbar onNew={handleNew} onEdit={handleEdit} disableEdit={selected.length !== 1} onDelete={handleDelete} disableDelete={selected.length === 0}>
          </Toolbar>
          <TableContainer isLoading={false}>
                              <DataTable head={<tr>
                  <th style={{ width: '3rem' }}>#</th><th>Descrição</th>
                </tr>}>
                <tbody>
                  {grouped.map(group => (
                    <>
                      <tr key={`nat_${group.natureza.id}`} style={{ backgroundColor: '#d1e7dd', pointerEvents: 'none' }}>
                        <td colSpan={2} className="fw-semibold small">
                          <i className="bi bi-tag me-2 text-success"></i>
                          {group.natureza.descricao}
                        </td>
                      </tr>
                      {group.tipos.map(tg => (
                        <>
                          <tr key={`tipo_${tg.tipoBem.idNatureza}_${tg.tipoBem.id}`} style={{ backgroundColor: '#e9f5e9', pointerEvents: 'none' }}>
                            <td colSpan={2} className="small" style={{ paddingLeft: '1.5rem' }}>
                              <i className="bi bi-box me-2 text-success"></i>
                              {tg.tipoBem.descricao}
                            </td>
                          </tr>
                          {tg.itens.map((item, idx) => (
                            <tr key={keyOf(item)} className={selected.find(s => keyOf(s) === keyOf(item)) ? 'table-success' : ''} onClick={() => toggleSelect(item)} style={{ cursor: 'pointer' }}>
                              <td className="text-muted" style={{ paddingLeft: '3rem' }}>{idx + 1}</td>
                              <td>{item.descricao}</td>
                            </tr>
                          ))}
                        </>
                      ))}
                    </>
                  ))}
                </tbody>
              </DataTable>
          </TableContainer>
        </PageContent>
        <PaginationBar setPagination={setPagination} />
      </ResponsiveShadow>
      <ItemModal show={showModal} onClose={() => setShowModal(false)} onSubmit={handleSave} form={form} handleChange={handleChange} naturezas={naturezas} tiposBens={tiposBens} />
      <DefaultModal {...setDefaultModal} />
      <ConfirmModal {...setConfirmModal} />
    </Layout>
  );
}

export default Itens;
